import React from "react";
import PropTypes from "prop-types";
import Helmet from "react-helmet";
import get from "lodash/get";
import serialize from "serialize-javascript";
import config from "../../../config";
import { isValidFAQData, buildFAQMetadata } from "../../lib/faq";
import { ProductDescriptionFAQType } from "../ProductDescriptionFAQ";

const Metadata = ({
  includeFacebook,
  includeTwitter,
  path,
  canonicalUrl: overrideCanonicalUrl,
  siteName,
  title,
  description,
  image,
  noIndex,
  keywords,
  type,
  alternateUrls,
  hideProductsNav,
  faq,
}) => {
  const site = siteName || "Redbubble";

  const facebookAppId = config("fbAppId");
  const baseUrl = config("rbmBaseWebUrl");
  const availableLocales = Object.values(config("availableLocales"));
  const canonicalUrl = overrideCanonicalUrl || `${baseUrl}${path}`;
  const hasAalternateUrls =
    Array.isArray(alternateUrls) && alternateUrls.length > 0;

  const shouldRenderFAQ = isValidFAQData(faq);
  const faqItems = get(faq, "items", []);

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {title && <meta name="og:title" content={`${title} | ${site}`} />}
      {description && <meta name="description" content={description} />}
      {description && <meta name="og:description" content={description} />}

      {shouldRenderFAQ && (
        <script type="application/ld+json" data-testid="faq-metadata">
          {serialize(buildFAQMetadata(faqItems), { unsafe: true })}
        </script>
      )}

      {keywords && <meta name="keywords" content={keywords} />}

      <meta name="og:type" content={type} />
      <meta name="og:site_name" content={site} />

      {image && <meta name="og:image" content={image} />}

      <meta name="og:url" content={canonicalUrl} />
      <link rel="canonical" href={canonicalUrl} />
      {hasAalternateUrls
        ? alternateUrls.map(({ locale, url }) => (
            <link key={locale} rel="alternate" href={url} hrefLang={locale} />
          ))
        : availableLocales.map((locale) => (
            <link
              key={locale.name}
              rel="alternate"
              href={`${baseUrl}${locale.urlSegment}${path}`}
              hrefLang={locale.name}
            />
          ))}

      {noIndex && <meta name="robots" content="noindex" />}

      {includeFacebook && <meta property="fb:app_id" content={facebookAppId} />}
      {includeTwitter && <meta name="twitter:site" content="@redbubble" />}
      {includeTwitter && (
        <meta name="twitter:card" content="summary_large_image" />
      )}
      {hideProductsNav && <body hide-nav="true" />}
    </Helmet>
  );
};

Metadata.propTypes = {
  path: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  includeTwitter: PropTypes.bool,
  includeFacebook: PropTypes.bool,
  image: PropTypes.string,
  noIndex: PropTypes.bool,
  keywords: PropTypes.string,
  canonicalUrl: PropTypes.string,
  siteName: PropTypes.string,
  type: PropTypes.string,
  alternateUrls: PropTypes.arrayOf(
    PropTypes.shape({
      locale: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  hideProductsNav: PropTypes.bool,
  faq: ProductDescriptionFAQType,
};

Metadata.defaultProps = {
  description: "",
  image: "",
  includeTwitter: false,
  includeFacebook: false,
  noIndex: false,
  keywords: "",
  canonicalUrl: "",
  siteName: "Redbubble",
  type: "website",
  alternateUrls: [],
  hideProductsNav: false,
  faq: null,
};

Metadata.displayName = "Metadata";

export default Metadata;
